import React, { createContext, useContext, useEffect, useState } from "react";
import type { PostContextType, PostStateProps } from "./types";
import { defaultValues } from "./defaultValues";
import ThemeColors from "../../../../../../themes/colors";
import { Post } from "../../types";
import { postService } from "../../../../../../services/modules/post";
import { companyGoalsService } from "../../../../../../services/modules/companyGoals";
import useEnterprise from "../../../../hooks/useEnterprise";
import { personaService } from "../../../../../../services/modules/persona";

const initialConfig = { ...defaultValues };

const PostContext = createContext<PostContextType>(initialConfig);

export const PostProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { enterpriseState } = useEnterprise();

  const [postState, setPostState] = useState<PostStateProps>(
    initialConfig.postState
  );

  const updateState = (newState: Partial<PostStateProps>) => {
    setPostState((prevState: PostStateProps) => ({
      ...prevState,
      ...newState,
    }));
  };

  const fetchCompanyGoals = (id: string) => {
    companyGoalsService
      .getCompanyGoals(id)
      .then((response) => updateState({ smartGoals: response }));
  };

  const fetchPersona = (id: string) => {
    personaService
      .getPersona(id)
      .then((response) => updateState({ persona: response }));
  };

  const fetchPosts = (id: string) => {
    postService.getPosts(id).then((response: Post[]) => {
      updateState({ posts: response });

      const definedView = defineView(response);

      updateState({ groups: definedView });
    });
  };

  const defineView = (posts: Post[]) => {
    const uniqueColors = [
      {
        color: ThemeColors.Purple500,
        backgroundColor: ThemeColors.Purple100,
        view: "triangle",
        title: "Objetivo 1",
      },
      {
        color: ThemeColors.Blue500,
        backgroundColor: ThemeColors.Blue100,
        view: "circle",
        title: "Objetivo 2",
      },
      {
        color: ThemeColors.Green500,
        backgroundColor: ThemeColors.Green100,
        view: "square",
        title: "Objetivo 3",
      },
    ];

    const uniqueGroups = Array.from(
      new Set(posts.map((item) => item.smart_goal_id))
    ).map((smartGoalId, index) => {
      const colorConfig = uniqueColors[index % uniqueColors.length];
      return {
        smartGoalId,
        ...colorConfig,
      };
    });

    return uniqueGroups;
  };

  useEffect(() => {
    if (enterpriseState.selectedEnterprise) {
      fetchPosts(enterpriseState.selectedEnterprise);
      fetchCompanyGoals(enterpriseState.selectedEnterprise);
      fetchPersona(enterpriseState.selectedEnterprise);
    }
  }, []);

  const value = {
    postState,
    updateState,
    fetchPosts
  };

  return <PostContext.Provider value={value}>{children}</PostContext.Provider>;
};

const usePost = () => useContext(PostContext);

export default usePost;
